import { FC, Fragment, useCallback, useContext } from 'react';
import { useSelector } from 'react-redux';
import { SearchTeamMembersForGroupQuery, Jane, StaticGroup } from 'types/graphql';
import { InfiniteScrollLoading } from 'components/InfiniteScrollLoading';
import {
  NoResultsWrapper,
  InfiniteScrollWrapper,
} from 'pages/Settings/modals/GroupsModal/components/SearchTeamMembersGroups/SearchTeamMembersGroups.styled';
import { useTranslation } from 'hooks/useTranslation';
import { TeamMemberOption } from 'pages/Settings/modals/GroupsModal/components/SearchTeamMembersGroups/src/TeamMemberOption';
import { SearchTeamMembersGroupsContext } from 'pages/Settings/modals/GroupsModal/components/SearchTeamMembersGroups/SearchTeamMembersGroups.context';
import { useGroupsModalFormContext } from 'pages/Settings/modals/GroupsModal/GroupsModal.form.context';
import {
  isMultiLocationCallOutFeatureEnabledSelector,
  isMultiLocationTieringFeatureEnabledSelector,
} from 'store/selectors/featureFlagSelectors';
import { getCurrentlySelectedLocation } from 'utils/getCurrentlySelectedLocation';
import { Flex } from 'components/Flex';

interface ITeamMembersListProps {
  groupMemberIds: string[];
  data: SearchTeamMembersForGroupQuery;
  name: string;
  onSelect: (e: unknown) => void;
  onLoadMore: () => void;
}

export const TeamMembersList: FC<ITeamMembersListProps> = ({
  groupMemberIds,
  data,
  name,
  onSelect,
  onLoadMore,
}) => {
  const { values: queryContextValues } = useContext(SearchTeamMembersGroupsContext);

  const { deletedMemberIds } = useGroupsModalFormContext();
  const { t } = useTranslation();
  const currentlySelectedLocation = getCurrentlySelectedLocation();
  const isMultiLocationCallOutFeatureEnabled = useSelector(
    isMultiLocationCallOutFeatureEnabledSelector(Number(currentlySelectedLocation?.value)),
  );
  const isMultiLocationTieringFeatureEnabled = useSelector(
    isMultiLocationTieringFeatureEnabledSelector(Number(currentlySelectedLocation?.value)),
  );
  const isInStaticGroup = useCallback(
    (staticGroups: StaticGroup[]) =>
      staticGroups
        .map((group) => group.displayId)
        .includes(queryContextValues.groupsFilter.withId[0]),
    [queryContextValues.groupsFilter.withId],
  );
  const isChecked = useCallback(
    (node: Jane) =>
      groupMemberIds.includes(node.id) ||
      (!deletedMemberIds.has(node.id) && isInStaticGroup(node.staticGroups)),
    [deletedMemberIds, groupMemberIds, isInStaticGroup],
  );

  if (!data || (data?.fetchJanes?.edges && data?.fetchJanes?.edges.length === 0))
    return <NoResultsWrapper>{t('common:NO_RESULTS')}</NoResultsWrapper>;

  return (
    <Fragment>
      {data?.fetchJanes?.edges &&
        data.fetchJanes.edges.map(({ node }) => {
          if (!node) return <Fragment key="empty" />;
          const isSharedJanePrimaryCommunityDifferentFromSelectedLocation =
            (!!isMultiLocationCallOutFeatureEnabled || !!isMultiLocationTieringFeatureEnabled) &&
            !node?.agencyCommunity &&
            node?.primaryCommunity?.id !== currentlySelectedLocation?.value?.toString();

          return (
            <Flex key={node.id} alignItems="center">
              <TeamMemberOption
                isChecked={isChecked(node as Jane)}
                node={node as Jane}
                name={name}
                onSelect={onSelect}
                showGroupIcon={isSharedJanePrimaryCommunityDifferentFromSelectedLocation}
              />
            </Flex>
          );
        })}
      {!!data?.fetchJanes?.pageInfo.hasNextPage ? (
        <InfiniteScrollWrapper>
          <InfiniteScrollLoading onViewportEnter={onLoadMore} />
        </InfiniteScrollWrapper>
      ) : null}
    </Fragment>
  );
};
